import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box, Paper, TextField, Button, Typography, Grid, Slider,
    FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
    ToggleButton, ToggleButtonGroup, Divider, useTheme, alpha
} from '@mui/material';
import QRCode from 'qrcode';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ContactsIcon from '@mui/icons-material/Contacts';
import WifiIcon from '@mui/icons-material/Wifi';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const QRCodeGenerator = () => {
    const theme = useTheme();
    const canvasRef = useRef(null);
    const [mode, setMode] = useState('url');
    const [text, setText] = useState('https://umbrellaco.in');
    const [size, setSize] = useState(256);
    const [fgColor, setFgColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('#ffffff');
    const [errorLevel, setErrorLevel] = useState('M');
    const [copied, setCopied] = useState(false);

    // vCard fields
    const [vCard, setVCard] = useState({
        name: 'John Doe',
        phone: '+1234567890',
        email: 'john@example.com',
        org: 'UmbrellaCo',
        title: 'Developer'
    });

    // WiFi fields
    const [wifi, setWifi] = useState({
        ssid: 'MyNetwork',
        password: 'password123',
        encryption: 'WPA'
    });

    const generateQRData = useCallback(() => {
        switch (mode) {
            case 'vcard':
                return `BEGIN:VCARD
VERSION:3.0
FN:${vCard.name}
TEL:${vCard.phone}
EMAIL:${vCard.email}
ORG:${vCard.org}
TITLE:${vCard.title}
END:VCARD`;
            case 'wifi':
                return `WIFI:T:${wifi.encryption};S:${wifi.ssid};P:${wifi.password};;`;
            default:
                return text;
        }
    }, [mode, vCard, wifi, text]);

    useEffect(() => {
        const generateQR = async () => {
            if (canvasRef.current) {
                try {
                    await QRCode.toCanvas(canvasRef.current, generateQRData(), {
                        width: size,
                        margin: 2,
                        color: {
                            dark: fgColor,
                            light: bgColor
                        },
                        errorCorrectionLevel: errorLevel
                    });
                } catch (err) {
                    console.error('QR generation error:', err);
                }
            }
        };
        generateQR();
    }, [text, size, fgColor, bgColor, errorLevel, mode, vCard, wifi, generateQRData]);

    const handleDownload = (format) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        if (format === 'png') {
            const link = document.createElement('a');
            link.download = 'qrcode.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        } else if (format === 'svg') {
            QRCode.toString(generateQRData(), {
                type: 'svg',
                color: { dark: fgColor, light: bgColor },
                errorCorrectionLevel: errorLevel
            }).then(svg => {
                const blob = new Blob([svg], { type: 'image/svg+xml' });
                const link = document.createElement('a');
                link.download = 'qrcode.svg';
                link.href = URL.createObjectURL(blob);
                link.click();
            });
        }
    };

    const handleCopyImage = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        try {
            canvas.toBlob(async (blob) => {
                await navigator.clipboard.write([
                    new ClipboardItem({ 'image/png': blob })
                ]);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    return (
        <>
            <SEO
                title="QR Code Generator | Create Custom QR Codes | UmbrellaCo"
                description="Generate free custom QR codes for URLs, text, vCards, and WiFi networks. Customize colors and size, then download as PNG or SVG."
                keywords="qr code generator, create qr code, wifi qr code, vcard qr code, custom qr code png svg"
                url="/dev-tools/qr-generator"
                jsonLd={createSoftwareAppSchema(
                    'QR Code Generator',
                    'Generate fully customizable QR codes for links, contacts, and WiFi.',
                    'https://umbrellaco.in/dev-tools/qr-generator',
                    ['QR Code', 'Image Generation', 'Utilities']
                )}
            />
            <ToolPageContainer
                title="QR Code Generator"
                description="Create dynamic, downloadable QR codes for websites, plain text, digital business cards (vCard), or automatic WiFi login configurations."
                disablePadding
            >
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
            <Grid container spacing={3}>
                {/* Left Panel - Options */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        {/* Mode Selector */}
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Type</Typography>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(e, v) => v && setMode(v)}
                            fullWidth
                            sx={{ mb: 3 }}
                        >
                            <ToggleButton value="url">
                                <LinkIcon sx={{ mr: 1 }} /> URL
                            </ToggleButton>
                            <ToggleButton value="text">
                                <TextFieldsIcon sx={{ mr: 1 }} /> Text
                            </ToggleButton>
                            <ToggleButton value="vcard">
                                <ContactsIcon sx={{ mr: 1 }} /> vCard
                            </ToggleButton>
                            <ToggleButton value="wifi">
                                <WifiIcon sx={{ mr: 1 }} /> WiFi
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* URL/Text Input */}
                        {(mode === 'url' || mode === 'text') && (
                            <TextField
                                fullWidth
                                label={mode === 'url' ? 'URL' : 'Text'}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                multiline={mode === 'text'}
                                rows={mode === 'text' ? 4 : 1}
                                sx={{ mb: 3 }}
                            />
                        )}

                        {/* vCard Fields */}
                        {mode === 'vcard' && (
                            <Box sx={{ mb: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Full Name" value={vCard.name}
                                            onChange={(e) => setVCard({ ...vCard, name: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Phone" value={vCard.phone}
                                            onChange={(e) => setVCard({ ...vCard, phone: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Email" value={vCard.email}
                                            onChange={(e) => setVCard({ ...vCard, email: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Organization" value={vCard.org}
                                            onChange={(e) => setVCard({ ...vCard, org: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Title" value={vCard.title}
                                            onChange={(e) => setVCard({ ...vCard, title: e.target.value })} />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* WiFi Fields */}
                        {mode === 'wifi' && (
                            <Box sx={{ mb: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Network Name (SSID)" value={wifi.ssid}
                                            onChange={(e) => setWifi({ ...wifi, ssid: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextField fullWidth label="Password" value={wifi.password}
                                            onChange={(e) => setWifi({ ...wifi, password: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Security</InputLabel>
                                            <Select value={wifi.encryption} label="Security"
                                                onChange={(e) => setWifi({ ...wifi, encryption: e.target.value })}>
                                                <MenuItem value="WPA">WPA/WPA2</MenuItem>
                                                <MenuItem value="WEP">WEP</MenuItem>
                                                <MenuItem value="nopass">None</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Customization */}
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>Customization</Typography>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Size: {size}px</Typography>
                            <Slider
                                value={size}
                                onChange={(e, v) => setSize(v)}
                                min={128}
                                max={512}
                                step={32}
                            />
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Foreground</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <input
                                        type="color"
                                        value={fgColor}
                                        onChange={(e) => setFgColor(e.target.value)}
                                        style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                                    />
                                    <TextField size="small" value={fgColor} onChange={(e) => setFgColor(e.target.value)} sx={{ flex: 1 }} />
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Background</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <input
                                        type="color"
                                        value={bgColor}
                                        onChange={(e) => setBgColor(e.target.value)}
                                        style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                                    />
                                    <TextField size="small" value={bgColor} onChange={(e) => setBgColor(e.target.value)} sx={{ flex: 1 }} />
                                </Box>
                            </Grid>
                        </Grid>

                        <FormControl fullWidth size="small">
                            <InputLabel>Error Correction</InputLabel>
                            <Select value={errorLevel} label="Error Correction" onChange={(e) => setErrorLevel(e.target.value)}>
                                <MenuItem value="L">Low (7%)</MenuItem>
                                <MenuItem value="M">Medium (15%)</MenuItem>
                                <MenuItem value="Q">Quartile (25%)</MenuItem>
                                <MenuItem value="H">High (30%)</MenuItem>
                            </Select>
                        </FormControl>
                    </Paper>
                </Grid>

                {/* Right Panel - Preview */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{
                        p: 3,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, alignSelf: 'flex-start' }}>Preview</Typography>

                        <Box sx={{
                            p: 3,
                            bgcolor: 'white',
                            borderRadius: 2,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                            mb: 3
                        }}>
                            <canvas ref={canvasRef} />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload('png')}
                            >
                                PNG
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload('svg')}
                            >
                                SVG
                            </Button>
                            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                                <IconButton onClick={handleCopyImage} color={copied ? 'success' : 'default'}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            </Box>
            </ToolPageContainer>
        </>
    );
};

export default QRCodeGenerator;
